import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

const EMOJI_SET = ['👍', '❤️', '😂', '😮', '😢', '😡', '👏', '🔥'];

interface Reaction {
  emoji: string;
  count: number;
  users: number[];
  user_reacted: boolean;
}

interface ReactionPickerProps {
  entityType: 'post' | 'message' | 'wall';
  entityId: number;
  align?: 'left' | 'right';
}

const ReactionPicker: React.FC<ReactionPickerProps> = ({ entityType, entityId, align = 'left' }) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const fetchReactions = async () => {
    try {
      const res = await api.get('/reactions/', {
        params: { entity_type: entityType, entity_id: entityId }
      });
      setReactions(res.data);
    } catch {}
  };

  useEffect(() => { fetchReactions(); }, [entityId]);

  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  const toggleReaction = async (emoji: string) => {
    const existing = reactions.find(r => r.emoji === emoji && r.user_reacted);
    try {
      if (existing) {
        await api.delete('/reactions/', {
          params: { entity_type: entityType, entity_id: entityId }
        });
      } else {
        await api.post('/reactions/', { entity_type: entityType, entity_id: entityId, emoji });
      }
      fetchReactions();
    } catch {}
    setShowPicker(false);
  };

  return (
    <div className="relative inline-flex items-center gap-1.5 flex-wrap">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          onClick={() => toggleReaction(r.emoji)}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition ${
            r.user_reacted ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-500'
          }`}
        >
          <span>{r.emoji}</span>
          <span className="font-medium">{r.count}</span>
        </button>
      ))}

      <div ref={pickerRef} className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="w-7 h-7 rounded-full bg-gray-800/50 border border-gray-700 text-gray-500 hover:text-white hover:border-gray-500 text-xs flex items-center justify-center transition"
        >
          +
        </button>

        {showPicker && (
          <div
            data-testid="reaction-picker-panel"
            className={`absolute bottom-full ${align === 'right' ? 'right-0' : 'left-0'} mb-2 bg-[#1a1d24] border border-gray-700 rounded-xl shadow-2xl p-2 flex gap-1 z-50`}
          >
            {EMOJI_SET.map((emoji) => (
              <button
                key={emoji}
                onClick={() => toggleReaction(emoji)}
                className="w-9 h-9 rounded-lg hover:bg-gray-700 text-xl flex items-center justify-center transition"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReactionPicker;
